import saveAs from 'file-saver';
import { apiCall_v2 } from '../Services/AxiosService';
import Constants from '../utils/Constants';
import dayjs from '../utils/dayjs';
import { date, formatDate } from './dateTime';
import { toast } from 'react-toastify';
import { GetApiFmByFlidReportsAndTokenApiArg } from '../Redux/Apis/Reports/baseReportsApi';

//NOTE for all further PowerBi Reports, add Databricks Table name here used for Reports BlobStorage Task
export enum ReportType {
    HospitalReport = 'HospitalReport',
    YardSheet = 'YardSheet',
}

export enum FileType {
    pdf = 'pdf',
    csv = 'csv',
}

export enum OutputType {
    excel = 'excel',
    csv = 'csv',
    json = 'json',
    pdf = 'pdf',
}

export enum ReportName {
    DailyChargesReport = 'DailyChargesReport',
    GetLoadedCommodityVarianceByDateRange = 'LoadedCommodityVarianceByDateRange',
    GetLoadedCommodityVarianceByDate = 'GetLoadedCommodityVarianceByDate',
    TreatmentsReport = 'TreatmentsReport',
    GetMedicalInventoryByDateRange = 'GetMedicalInventoryByDateRange',
    MedicalItemPricingReport = 'MedicalItemPricingReport',
    GetReceiveRecordsByDateRange = 'GetReceiveRecordsByDateRange',
    GetShipRecordsByDateRange = 'GetShipRecordsByDateRange',
    GetRealizerHistoryByDateRange = 'GetRealizerHistoryByDateRange',
    GetCommodityUsageData = 'GetCommodityUsageData',
    AsFedReport = 'AsFedReport',
    FeedAmountBilledByLot = 'FeedAmountBilledByLot',
    PenRiderReport = 'PenRiderReport',
    ScheduleAndRationChangeReport_Split = 'ScheduleAndRationChangeReport_Split',
    ScheduleAndRationChangeReport_VariableSplit = 'ScheduleAndRationChangeReport_VariableSplit',
    LoaderReport = 'LoaderReport',
    EidLotReport = 'EidLotReport',
    GetInvoiceSummaryByDateRange = 'GetInvoiceSummaryByDateRange',
    VetYardSheetReport = 'VetYardSheetReport',
    YardSheetReport = 'YardSheetReport',
    CommodityPricingReport = 'CommodityPricingReport',
    GetCommodityHistory = 'GetCommodityHistory',
    GetCommoditiesUsedVersusBilled = 'GetCommoditiesUsedVersusBilled',
    ExportScaleTickets = 'ExportScaleTickets',
    ActualVersusExpectedFeedChargesByLot = 'ActualVersusExpectedFeedChargesByLot',
    FeedOutOfVariance = 'FeedOutOfVariance',
    DriverVarianceReportByDateRange = 'DriverVarianceReportByDateRange',
    LotHealthReport = 'LotHealthReport',
    DashboardCattleHeadCounts = 'DashboardCattleHeadCounts',
    DashboardCattleOwners = 'DashboardCattleOwners',
    DashboardFeedingEvent = 'DashboardFeedingEvent',
    DashboardFeedingStatus = 'DashboardFeedingStatus',
    DashboardFeedingTotalMfe = 'DashboardFeedingTotalMfe',
    DashboardMovements = 'DashboardMovements',
    DashboardHealth = 'DashboardHealth',
    DashboardMovementsv2 = 'DashboardMovementsv2',
    DashboardHealthv2 = 'DashboardHealthv2',
    MovementHistoryReport = 'MovementHistoryByDateRange',
    TreatmentMoveToCattleReport = 'TreatmentMoveToCattleReport',
    FeedlotMovements = 'FeedlotMovements',
    DashboardOutOfSyncFeedcalls = 'OutOfSyncFeedCalls',
    FeedlotMovementsAndHdCounts = 'FeedlotMovementsAndHdCounts',
}

export interface ReportParams {
    reportType: ReportType;
    fileType: FileType;
    filterDate: string | date;
}

export const GetPowerBiReport = async (
    feedlotId: number,
    reportParams: ReportParams = {
        reportType: ReportType.HospitalReport,
        fileType: FileType.csv,
        filterDate: formatDate(dayjs()),
    },
) => {
    const response = await apiCall_v2({
        method: 'get',
        url: `${Constants.apiUrls.FEED_LOT_MANAGER}/${feedlotId}${Constants.apiUrls.POWERBI_REPORTS}?reportType=${reportParams.reportType}&fileType=${reportParams.fileType}&filterDate=${reportParams.filterDate}`,
        isResRequired: true,
    });
    if (response) {
        const blob = new Blob([response.data]);
        saveAs(blob, `${reportParams.reportType}.${reportParams.fileType}`);
    }
};

export const downloadExcelReport = async <T>(
    feedlotId: number,
    successMessage: string,
    downloadStartedMessage: string,
    errorMessage: string,
    noDataMessage: string,
    fileName: string,
    reportParameters: T,
    triggerDownloadFunction: (arg0: T) => any,
) => {
    if (feedlotId) {
        toast.info(downloadStartedMessage);

        const report = await triggerDownloadFunction(reportParameters);
        if (report.isError && 'status' in report.error) {
            if (report.error.status === Constants.responseCode.NOT_FOUND) {
                toast.error(noDataMessage);
            } else {
                toast.error(errorMessage);
            }
        } else {
            if (report.data) {
                saveAs(
                    report.data,
                    `${fileName}${Constants.exportExcelConstant.fileExtension}`,
                );
                toast.success(successMessage);
            }
        }
    }
};

const activePolls = new Map<
    string,
    {
        timeoutId: NodeJS.Timeout;
        startTime: number;
    }
>();

export const downloadExcelReportAsync = async <T>(
    feedlotId: number,
    successMessage: string,
    downloadStartedMessage: string,
    errorMessage: string,
    noDataMessage: string,
    fileName: string,
    reportParameters: T,
    postReportRequestFunction: (arg0: T) => any,
    getReportFunction: (arg0: GetApiFmByFlidReportsAndTokenApiArg) => any,
) => {
    if (feedlotId) {
        const token = await postReportRequestFunction(reportParameters);

        if (token.isError) {
            toast.error(errorMessage);
            return;
        }

        if (token.data) {
            toast.info(downloadStartedMessage);

            if (activePolls.has(token.data)) {
                const existingJob = activePolls.get(token.data);
                if (existingJob) {
                    clearTimeout(existingJob.timeoutId);
                }
            }

            let pollInterval = 3000;
            const maxPollTime = 300000;
            const pollStartTime = dayjs().valueOf();

            const pollReport = async () => {
                const poll = activePolls.get(token.data);
                if (!poll || !poll?.startTime) {
                    return;
                }

                const elapsedTime = dayjs().valueOf() - poll.startTime;

                if (elapsedTime > maxPollTime) {
                    toast.error(errorMessage);
                    activePolls.delete(token.data);
                    return;
                }

                if (elapsedTime <= 30000) {
                    pollInterval = 3000;
                } else if (elapsedTime <= 60000) {
                    pollInterval = 5000;
                } else {
                    pollInterval = 10000;
                }

                try {
                    const result = await getReportFunction({
                        token: token.data,
                        flid: feedlotId.toString(),
                    });

                    if (
                        result.isSuccess &&
                        result.data instanceof Blob &&
                        result.data.size > 0
                    ) {
                        saveAs(
                            result.data,
                            `${fileName}${Constants.exportExcelConstant.fileExtension}`,
                        );
                        toast.success(successMessage);
                        activePolls.delete(token.data);
                        return;
                    } else if (result.isSuccess) {
                        if (activePolls.has(token.data)) {
                            const timeoutId = setTimeout(
                                pollReport,
                                pollInterval,
                            );
                            activePolls.set(token.data, {
                                timeoutId,
                                startTime: poll.startTime,
                            });
                        }
                    } else {
                        if (
                            'status' in result.error &&
                            result.error.status ===
                                Constants.responseCode.NOT_FOUND
                        ) {
                            toast.error(noDataMessage);
                        } else {
                            toast.error(errorMessage);
                        }
                        activePolls.delete(token.data);
                        return;
                    }
                } catch (error) {
                    toast.error(errorMessage);
                    activePolls.delete(token.data);
                }
            };

            const timeoutId = setTimeout(pollReport, pollInterval);
            activePolls.set(token.data, {
                timeoutId,
                startTime: pollStartTime,
            });
        }
    }
};
